import { useCallback } from 'react'

import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'
import { TaskStatus } from '@shared/utils'
import { isEscalationTask } from '@shared/utils/src/TasksHelper/TasksHelper'

import { hasDoneReasons, useTaskDoneReasons } from '@hooks/useTaskDoneReasons'

const COMPLETION_TITLE = 'Confirm Task Completion'
const EXPIRATION_TITLE = 'Confirm Task Expiration'

/**
 * Resolves the confirmation needed to move a task to a given status.
 *
 * @param provider - true when the actor is a provider expiring their own task, which offers a
 *                   shorter, provider-specific reason list than the one the kanban shows.
 * @returns {(task, targetStatus) => Config | null} - `null` means no confirmation is needed.
 *
 * Config shape:
 *   { title, description?, reason? }
 *   reason: { type: 'select', label, options, required } | { type: 'text', label, required, multiline }
 */
export function useStatusChangeConfig({ provider = false } = {}) {
  const getDoneReasons = useTaskDoneReasons()
  const adminExpireReasons = useLookup(Lookup.TaskExpiredReasons)
  const providerExpireReasons = useLookup(Lookup.ProviderTaskExpiredReasons)

  const expireReasons = provider ? providerExpireReasons : adminExpireReasons

  return useCallback(
    (task, targetStatus) => {
      if (targetStatus === TaskStatus.Completed) {
        if (isEscalationTask(task)) {
          return {
            title: COMPLETION_TITLE,
            reason: { type: 'text', label: 'Reason for completion', required: true, multiline: true },
          }
        }
        if (hasDoneReasons(task.kind)) {
          return {
            title: COMPLETION_TITLE,
            reason: { type: 'select', label: 'Please select a reason', options: getDoneReasons(task.kind), required: true },
          }
        }
        return {
          title: COMPLETION_TITLE,
          description:
            'Are you sure you want to change this task status to "Done"?\nOnce updated, it cannot be changed and no further actions will be available.',
        }
      }

      if (targetStatus === TaskStatus.Expired) {
        return {
          title: EXPIRATION_TITLE,
          description: 'Please select a reason for expiring this task:',
          reason: { type: 'select', label: 'Select Reason', options: expireReasons, required: true },
        }
      }

      return null
    },
    [expireReasons, getDoneReasons]
  )
}
